import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Send, Bot, User, Sparkles, FlaskConical, Leaf, FileText, RotateCcw, ThumbsUp, ThumbsDown, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card } from "../ui/card";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { ScrollArea } from "../ui/scroll-area";
// Badge available if needed: import { Badge } from "../ui/badge";
import { cn } from "../../lib/utils";
import FeedbackBar from "../FeedbackBar/FeedbackBar";

// Thinking phrases for the loading indicator
const THINKING_PHRASES = [
    "Processing data",
    "Analyzing results",
    "Finding information",
    "Checking database",
    "Computing response",
    "Searching records",
    "Reviewing data",
    "Gathering information",
    "Processing request",
    "Checking sources"
];

// Question categories with icons
const questionCategories = {
    Potency: {
        icon: Sparkles,
        color: "text-amber-500",
        bgColor: "bg-amber-50 hover:bg-amber-100 dark:bg-amber-950/30 dark:hover:bg-amber-950/50",
        questions: [
            "What is my average potency for my most tested strain?",
            "What strains are the highest in average THC and which strains are the lowest?",
            "What strain produces the highest minor cannabinoid percentage?",
            "What is the high/low potency range for the most tested strain?",
            "Are my potency results increasing or decreasing compared to historical data?"
        ]
    },
    Terpenes: {
        icon: Leaf,
        color: "text-emerald-500",
        bgColor: "bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-950/30 dark:hover:bg-emerald-950/50",
        questions: [
            "What are my most common terpenes?",
            "What are the top 3 terpenes for a random strain?",
            "What strain produces limonene as the dominant terpene?",
            "Separate samples submitted in the last 6 months by top 3 terpenes in profile.",
            "Has there been a decrease in terpene profile concentration compared to previous results?"
        ]
    },
    Microbials: {
        icon: FlaskConical,
        color: "text-rose-500",
        bgColor: "bg-rose-50 hover:bg-rose-100 dark:bg-rose-950/30 dark:hover:bg-rose-950/50",
        questions: [
            "List samples that failed micro in the last 6 months.",
            "Which strain has the most frequent microbial failures?",
            "What is my average microbial/Y&M per strain failure rate for each month?",
            "What strain is most resilient to aspergillus?",
            "Do you have any recommendations for mitigating future failures?"
        ]
    },
    COA: {
        icon: FileText,
        color: "text-blue-500",
        bgColor: "bg-blue-50 hover:bg-blue-100 dark:bg-blue-950/30 dark:hover:bg-blue-950/50",
        questions: [
            "How many COAs took longer than 4 days to report?",
            "How long will it take non-tested samples to be completed?",
            "What is the fastest COA turn around time and what was the slowest COA turn around time?",
            "What is holding up my COA from being completed?"
        ]
    }
};

const MainChat = ({ chats, formData, handleInputChange, handleFormSubmit, deletebtn, isTyping }) => {
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const categoryButtonRefs = useRef({});
    const dropdownRef = useRef(null);
    const [activeCategory, setActiveCategory] = useState(null);
    const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
    const [selectedFeedback, setSelectedFeedback] = useState(null);
    const [thinkingPhrase, setThinkingPhrase] = useState("Thinking");
    const [feedbackState, setFeedbackState] = useState({});
    const [dropdownPosition, setDropdownPosition] = useState({ left: 0, top: 0, ready: false, category: null });

    // Effect for cycling through thinking phrases
    useEffect(() => {
        if (!isTyping) return;
        setThinkingPhrase("Thinking");
        
        const interval = setInterval(() => {
            const randomIndex = Math.floor(Math.random() * THINKING_PHRASES.length);
            setThinkingPhrase(THINKING_PHRASES[randomIndex]);
        }, 3000);

        return () => clearInterval(interval);
    }, [isTyping]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [chats, isTyping]);

    useEffect(() => {
        if (!isFeedbackOpen) {
            setSelectedFeedback(null);
        }
    }, [isFeedbackOpen]);

    // Handle click outside dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (activeCategory && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                // Check if click is on a category button
                const clickedButton = Object.values(categoryButtonRefs.current).find(
                    (ref) => ref && ref.contains(event.target)
                );
                if (!clickedButton) {
                    setActiveCategory(null);
                    setDropdownPosition({ left: 0, top: 0, ready: false, category: null });
                }
            }
        };

        if (activeCategory) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
    }, [activeCategory]);

    const handleCategoryClick = (category) => {
        if (activeCategory === category) {
            setActiveCategory(null);
            setDropdownPosition({ left: 0, top: 0, ready: false, category: null });
        } else {
            setActiveCategory(category);
        }
    };

    // Calculate position synchronously before paint when category changes
    useLayoutEffect(() => {
        if (activeCategory) {
            const button = categoryButtonRefs.current[activeCategory];
            if (button) {
                const rect = button.getBoundingClientRect();
                setDropdownPosition({
                    left: rect.left,
                    top: rect.top - 8,
                    ready: true,
                    category: activeCategory
                });
            }
        } else {
            setDropdownPosition({ left: 0, top: 0, ready: false, category: null });
        }
    }, [activeCategory]);

    const handleQuestionSelect = (question) => {
        formData.message = question;
        handleInputChange({ target: { name: 'message', value: question } });
        setActiveCategory(null);
        inputRef.current?.focus();
    };

    const handleFeedback = (message, rating, index) => {
        setFeedbackState(prev => ({ ...prev, [index]: rating }));
        setSelectedFeedback({
            message: message.message,
            sender: message.sender,
            rating: rating,
            index: index
        });
        setIsFeedbackOpen(true);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleFormSubmit(e);
        }
    };

    return (
        <div className="flex flex-col h-full bg-gradient-to-b from-background to-muted/20">
            {/* Header */}
            <div className="flex-none px-6 py-4 border-b bg-background/80 backdrop-blur-sm">
                <div className="max-w-4xl mx-auto">
                    <div className="flex items-center gap-3">
                        <div className="p-2 rounded-xl bg-primary/10">
                            <Bot className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                            <h1 className="text-xl font-semibold tracking-tight">Chat with KAI</h1>
                            <p className="text-sm text-muted-foreground">Your AI Lab Assistant</p>
                            <p className="text-xl font-semibold tracking-tight">Last Updated: 1/22/2026</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Messages Area */}
            <ScrollArea className="flex-1 px-4 py-6">
                <div className="max-w-4xl mx-auto space-y-6">
                    {chats.length === 0 && !isTyping ? (
                        <div className="flex flex-col items-center justify-center py-12 space-y-6">
                            <div className="p-4 rounded-2xl bg-primary/10">
                                <Bot className="h-12 w-12 text-primary" />
                            </div>
                            <div className="text-center space-y-2">
                                <h2 className="text-2xl font-semibold">Welcome to KAI</h2>
                                <p className="text-muted-foreground max-w-md">
                                    I'm your AI lab assistant. Ask me about your COAs, potency data, terpene profiles, or test results.
                                </p>
                            </div>
                            
                            {/* Quick Actions Grid */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 w-full max-w-2xl mt-6">
                                {Object.entries(questionCategories).map(([category, { icon: Icon, color, bgColor }]) => (
                                    <button
                                        key={category}
                                        onClick={() => handleCategoryClick(category)}
                                        className={cn(
                                            "flex flex-col items-center gap-2 p-4 rounded-xl border transition-all duration-200",
                                            bgColor,
                                            activeCategory === category && "ring-2 ring-primary"
                                        )}
                                    >
                                        <Icon className={cn("h-6 w-6", color)} />
                                        <span className="text-sm font-medium">{category}</span>
                                    </button>
                                ))}
                            </div>

                            {/* Question Dropdown */}
                            {activeCategory && (
                                <Card className="w-full max-w-2xl p-2 animate-fade-in">
                                    <div className="space-y-1">
                                        {questionCategories[activeCategory].questions.map((question, idx) => (
                                            <button
                                                key={idx}
                                                onClick={() => handleQuestionSelect(question)}
                                                className="w-full text-left px-4 py-3 rounded-lg hover:bg-muted transition-colors text-sm"
                                            >
                                                {question}
                                            </button>
                                        ))}
                                    </div>
                                </Card>
                            )}
                        </div>
                    ) : (chats.length > 0 || isTyping) && (
                        <>
                            {chats.map((chat, index) => (
                                <div
                                    key={index}
                                    className={cn(
                                        "flex gap-3 animate-fade-in",
                                        chat.sender === "user" ? "flex-row-reverse" : "flex-row"
                                    )}
                                >
                                    {/* Avatar */}
                                    <Avatar className={cn(
                                        "h-8 w-8 flex-shrink-0",
                                        chat.sender === "user" ? "bg-primary" : "bg-muted"
                                    )}>
                                        <AvatarFallback className={chat.sender === "user" ? "bg-primary text-primary-foreground" : "bg-muted"}>
                                            {chat.sender === "user" ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                                        </AvatarFallback>
                                    </Avatar>

                                    {/* Message Bubble */}
                                    <div className={cn(
                                        "flex flex-col gap-2 max-w-[80%]",
                                        chat.sender === "user" ? "items-end" : "items-start"
                                    )}>
                                        <div className={cn(
                                            "px-4 py-3 rounded-2xl",
                                            chat.sender === "user" 
                                                ? "bg-primary text-primary-foreground rounded-tr-md" 
                                                : "bg-card border shadow-sm rounded-tl-md"
                                        )}>
                                            {chat.sender === "bot" ? (
                                                <div className="prose prose-sm dark:prose-invert max-w-none">
                                                    <ReactMarkdown>{String(chat.message || '')}</ReactMarkdown>
                                                </div>
                                            ) : (
                                                <p className="text-sm">{chat.message}</p>
                                            )}
                                        </div>

                                        {/* Feedback Buttons for Bot Messages */}
                                        {chat.sender === "bot" && (
                                            <div className="flex items-center gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className={cn(
                                                        "h-7 px-2 text-xs",
                                                        feedbackState[index] === 5 && "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                                    )}
                                                    onClick={() => handleFeedback(chat, 5, index)}
                                                >
                                                    <ThumbsUp className="h-3.5 w-3.5 mr-1" />
                                                    Good
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className={cn(
                                                        "h-7 px-2 text-xs",
                                                        feedbackState[index] === 1 && "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                                                    )}
                                                    onClick={() => handleFeedback(chat, 1, index)}
                                                >
                                                    <ThumbsDown className="h-3.5 w-3.5 mr-1" />
                                                    Bad
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}

                            {/* Typing Indicator */}
                            {isTyping && (
                                <div className="flex gap-3 animate-fade-in">
                                    <Avatar className="h-8 w-8 bg-muted">
                                        <AvatarFallback className="bg-muted">
                                            <Bot className="h-4 w-4" />
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="px-4 py-3 rounded-2xl rounded-tl-md bg-card border shadow-sm">
                                        <div className="flex items-center gap-2">
                                            <Loader2 className="h-4 w-4 animate-spin text-primary" />
                                            <span className="text-sm text-muted-foreground">{thinkingPhrase}</span>
                                            <span className="flex gap-0.5">
                                                <span className="w-1.5 h-1.5 bg-muted-foreground/50 rounded-full animate-pulse" style={{ animationDelay: '0ms' }} />
                                                <span className="w-1.5 h-1.5 bg-muted-foreground/50 rounded-full animate-pulse" style={{ animationDelay: '150ms' }} />
                                                <span className="w-1.5 h-1.5 bg-muted-foreground/50 rounded-full animate-pulse" style={{ animationDelay: '300ms' }} />
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                    <div ref={messagesEndRef} />
                </div>
            </ScrollArea>

            {/* Quick Actions (when there are messages) */}
            {chats.length > 0 && (
                <div className="flex-none px-4 py-2 border-t bg-background/50">
                    <div className="max-w-4xl mx-auto">
                        <div className="flex items-start gap-2 overflow-x-auto py-1 scrollbar-thin relative">
                            {Object.entries(questionCategories).map(([category, { icon: Icon, color }]) => (
                                <div key={category} className="flex-shrink-0">
                                    <Button
                                        ref={(el) => { categoryButtonRefs.current[category] = el; }}
                                        variant={activeCategory === category ? "secondary" : "ghost"}
                                        size="sm"
                                        onClick={() => handleCategoryClick(category)}
                                    >
                                        <Icon className={cn("h-4 w-4 mr-1.5", color)} />
                                        {category}
                                    </Button>
                                </div>
                            ))}
                            
                            <div className="flex-1" />
                            
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={deletebtn}
                                className="flex-shrink-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                            >
                                <RotateCcw className="h-4 w-4 mr-1.5" />
                                Clear Chat
                            </Button>
                        </div>
                        
                        {/* Dropdown rendered outside overflow container */}
                        {activeCategory && dropdownPosition.ready && dropdownPosition.category === activeCategory && (
                            <div 
                                ref={dropdownRef}
                                className="fixed z-[100]"
                                style={{
                                    left: `${dropdownPosition.left}px`,
                                    top: `${dropdownPosition.top}px`,
                                    transform: 'translateY(-100%)'
                                }}
                            >
                                <Card className="w-80 p-2 shadow-lg bg-background border">
                                    <ScrollArea className="max-h-48">
                                        <div className="space-y-1">
                                            {questionCategories[activeCategory].questions.map((question, idx) => (
                                                <button
                                                    key={idx}
                                                    onClick={() => handleQuestionSelect(question)}
                                                    className="w-full text-left px-3 py-2 rounded-md hover:bg-muted transition-colors text-sm"
                                                >
                                                    {question}
                                                </button>
                                            ))}
                                        </div>
                                    </ScrollArea>
                                </Card>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Input Area */}
            <div className="flex-none px-4 py-4 border-t bg-background">
                <form onSubmit={handleFormSubmit} className="max-w-4xl mx-auto">
                    <div className="flex gap-2">
                        <Input
                            ref={inputRef}
                            type="text"
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            placeholder="Ask KAI about your lab data..."
                            className="flex-1 h-12 px-4 text-base rounded-xl"
                            disabled={isTyping}
                        />
                        <Button 
                            type="submit" 
                            size="lg"
                            className="h-12 px-6 rounded-xl"
                            disabled={isTyping || !formData.message.trim()}
                        >
                            <Send className="h-5 w-5" />
                            <span className="sr-only">Send message</span>
                        </Button>
                    </div>
                </form>
            </div>

            {/* Feedback Bar */}
            <FeedbackBar
                conversationId={null}
                isOpen={isFeedbackOpen}
                onToggle={() => setIsFeedbackOpen(!isFeedbackOpen)}
                messages={chats}
                preSelectedMessage={selectedFeedback}
                initialRating={selectedFeedback?.rating}
                selectedMessageIndex={selectedFeedback?.index}
            />
        </div>
    );
};

export default MainChat;
